import { TextField } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  deleteEvent,
  getEvents,
  postEvent,
  putEvent,
} from "../../../api/events";
import Box from "@mui/material/Box";
import { DataGrid } from "@mui/x-data-grid";
import Button from "@mui/material/Button";
import { format } from "date-fns";
import { APIS, PERMISSIONS, SPORTS } from "../../../utils/constants";
import hasPermissions from "../../../utils/hasPermissions";
import ManageEventDialog from "../manageEventDialog";
import ConfirmationDialog from "../../common/confirmationDialog";

const createColumns = (handleOpenDialog, handleOpenConfirmation) => {
  return [
    {
      field: "id",
      headerName: "ID",
      width: 100,
    },
    {
      field: "name",
      headerName: "Name",
      width: 300,
      sortable: false
    },
    {
      field: "status",
      headerName: "Status",
      width: 100,
    },
    {
      field: "date",
      headerName: "Date",
      width: 200,
      renderCell: (row) => (
        <span>
          {format(new Date(row.row.date), "dd MMM yyyy HH:mm", {
            timeZone: "UTC",
          })}{" "}
          UTC
        </span>
      ),
    },
    {
      field: "competition",
      headerName: "Competition",
      width: 200,
      sortable: false
    },
    {
      field: "league",
      headerName: "League",
      width: 200,
    },
    {
      field: "sport",
      headerName: "Sport",
      width: 150,
      sortable: false
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 170,
      sortable: false,
      renderCell: (row) => (
        <Box
          sx={{
            display: "flex",
            gap: "10px",
          }}
        >
          <Button
            variant="contained"
            color="primary"
            disabled={!hasPermissions(PERMISSIONS.ALLOW_WRITE_EVENTS)}
            onClick={() => handleOpenDialog(row.row)}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={!hasPermissions(PERMISSIONS.ALLOW_WRITE_EVENTS)}
            onClick={() => handleOpenConfirmation(row.row)}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];
};

export function EventTab() {
  const [events, setEvents] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [activeEvent, setActiveEvent] = useState(null);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [sortOptions, setSortOptions] = useState([
    { field: "sport", sort: "asc" },
  ]);

  const [filters, setFilters] = useState({
    competition: "",
  });

  const [tempFilters, setTempFilters] = useState({
    competition: "",
  });

  const handleOpenDialog = useCallback((item) => {
    setActiveEvent(item);
    setIsDialogOpen(true);
  }, []);

  const createParams = useCallback(() => {
    const filterParams = [];
    Object.keys(filters).forEach((key) => {
      if (!filters[key]) return;
      filterParams.push({ key, value: filters[key] });
    });
    const pageParams = [
      { key: "take", value: pageSize },
      { key: "skip", value: page * pageSize },
    ];
    const sortParams = [
      { key: "sortBy", value: sortOptions?.[0]?.field || "name" },
      { key: "sortDirection", value: sortOptions?.[0]?.sort || "asc" },
    ];
    const sourceParams = [
      { key: "source", value: APIS.INTERNAL },
      { key: "sportId", value: SPORTS.CRICKET },
    ];
    return [...filterParams, ...pageParams, ...sortParams, ...sourceParams];
  }, [filters, page, pageSize, sortOptions]);

  const fetchEventsList = useCallback(async () => {
    const queryParams = createParams();
    const events = await getEvents(queryParams);
    setEvents(events.data);
    setTotal(events.total);
  }, [createParams]);

  const handleFiltersChange = useCallback(
    (event) => {
      const { name, value } = event.target;
      setTempFilters({ ...tempFilters, [name]: value });
    },
    [tempFilters]
  );

  const handleSearchClick = useCallback(() => {
    setPage(0);
    setFilters(tempFilters);
  }, [tempFilters]);

  const handleSortModelChange = useCallback((sortModel) => {
    setPage(0);
    setSortOptions([...sortModel]);
  }, []);

  const handleResetFilters = useCallback(async () => {
    setFilters({
      competition: "",
    });
    setTempFilters({
      competition: "",
    });
    setPage(0);
    await fetchEventsList();
  }, [fetchEventsList]);

  const handlePageSizeChange = useCallback((newPageSize) => {
    setPage(0);
    setPageSize(newPageSize);
  }, []);

  const handleOpenConfirmation = useCallback((item) => {
    setActiveEvent(item);
    setIsConfirmationOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsDialogOpen(false);
    setIsConfirmationOpen(false);
    setActiveEvent(null);
  }, []);

  const handleAddEvent = useCallback(async (event) => {
    await postEvent(event);
    await fetchEventsList();
    handleClose();
  }, [handleClose, fetchEventsList]);

  const handleEditEvent = useCallback(async (event) => {
    const data = event;
    const id = event.id;
    await putEvent(id, data);
    await fetchEventsList();
    handleClose();
  }, [handleClose, fetchEventsList]);

  const handleConfirm = useCallback((event) => {
    if (event.id) {
      return handleEditEvent(event);
    }
    handleAddEvent(event);
  }, [handleAddEvent, handleEditEvent]);

  const handleDeleteConfirmation = useCallback(async () => {
    await deleteEvent(activeEvent.id);
    await fetchEventsList();
    handleClose();
  }, [fetchEventsList, handleClose, activeEvent]);

  useEffect(() => {
    async function fetchData() {
      await fetchEventsList();
    }

    fetchData();
  }, [createParams, fetchEventsList]);

  const columns = useMemo(() => {
    return createColumns(handleOpenDialog, handleOpenConfirmation);
  }, [handleOpenDialog, handleOpenConfirmation]);

  return (
    <Box sx={{ textAlign: "center" }}>
      <h1>Matches</h1>
      <Button
        variant="contained"
        color="primary"
        disabled={!hasPermissions(PERMISSIONS.ALLOW_WRITE_EVENTS)}
        onClick={() => handleOpenDialog()}
      >
        Add new match
      </Button>
      {isConfirmationOpen && <ConfirmationDialog
        open={isConfirmationOpen}
        title={"Confirm"}
        content={"Do you want to delete this competition?"}
        onClose={handleClose}
        onConfirm={handleDeleteConfirmation}
      />}
      {isDialogOpen && <ManageEventDialog open={isDialogOpen} onConfirm={handleConfirm} onClose={handleClose} event={activeEvent} />}
      {events && (
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: "12px",
              padding: "20px 0",
            }}
          >
            filter by
            <TextField
              name="competition"
              variant="outlined"
              size="small"
              label="Competition"
              value={tempFilters.competition}
              onChange={(event) => handleFiltersChange(event)}
            />
            <Button variant="contained" onClick={() => handleSearchClick()}>
              Search
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => handleResetFilters()}
            >
              Reset
            </Button>
          </Box>
          <Box sx={{ height: "700px", width: "100%" }}>
            <DataGrid
              rows={events}
              columns={columns}
              rowCount={total}
              pageSize={pageSize}
              page={page}
              paginationMode="server"
              onPageChange={setPage}
              onPageSizeChange={handlePageSizeChange}
              rowsPerPageOptions={[25, 50, 100]}
              sortingMode="server"
              onSortModelChange={handleSortModelChange}
              disableSelectionOnClick
              disableColumnFilter
              experimentalFeatures={{ newEditingApi: true }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}
